<h2 mat-dialog-title class="dialog-title">
  <mat-icon>sync_alt</mat-icon>
  <span>Outsourcing Handoff — {{ data.contract.name || data.contract.id }}</span>
  <span class="spacer"></span>
  @if (handoff) {
  <button mat-icon-button (click)="load()" matTooltip="Refresh">
    <mat-icon>refresh</mat-icon>
  </button>
  }
  <button mat-icon-button (click)="close()">
    <mat-icon>close</mat-icon>
  </button>
</h2>

<mat-dialog-content>
  @if (loading && !handoff) {
  <div class="loading-container">
    <mat-spinner diameter="36"></mat-spinner>
    <p>Loading handoff...</p>
  </div>
  } @else if (loadError) {
  <div class="error-banner">
    <mat-icon>error_outline</mat-icon>
    <span>Failed to load handoff status.</span>
  </div>
  } @else if (notFound) {
  <div class="empty-state">
    <p>No outsourcing handoff exists for this contract yet.</p>
    <button mat-raised-button color="primary" [disabled]="starting" (click)="startHandoff()">
      @if (starting) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
      @else { <mat-icon>play_arrow</mat-icon> }
      Start Handoff
    </button>
    @if (startError) {
    <div class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ startError }}</span>
    </div>
    }
  </div>
  } @else if (handoff) {
  <div class="handoff-details">
    <div class="status-row">
      <span [class]="'status-chip ' + getStatusClass(handoff.status)">{{ handoff.status }}</span>
      <span class="status-desc">{{ statusDescription }}</span>
    </div>

    @if (actionRequiredDescription) {
    <div class="action-banner">
      <mat-icon>info_outline</mat-icon>
      <span>{{ actionRequiredDescription }}</span>
    </div>
    }

    @if (handoff.failure_reason) {
    <div class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ handoff.failure_reason }}</span>
    </div>
    }

    <div class="details-grid">
      <div class="detail">
        <span class="label">Provider</span>
        <span class="value">{{ handoff.provider }}</span>
      </div>
      <div class="detail">
        <span class="label">Reference</span>
        <span class="value">{{ handoff.provider_reference }}</span>
      </div>
      @if (handoff.provider_order_id) {
      <div class="detail">
        <span class="label">Provider Order</span>
        <span class="value">{{ handoff.provider_order_id }}</span>
        <button mat-stroked-button (click)="copyBuyerLink()">
          <mat-icon>link</mat-icon>
          Copy buyer tracking link
        </button>
      </div>
      }
      <div class="detail">
        <span class="label">Requested Coins</span>
        <span class="value">{{ formatCoins(handoff.requested_coins) }}</span>
      </div>
      <div class="detail">
        <span class="label">Local Sent / Received</span>
        <span class="value">{{ formatCoins(handoff.local_sent_coins) }} / {{ formatCoins(handoff.local_received_coins) }}</span>
      </div>
      <div class="detail">
        <span class="label">Provider Sent / Received</span>
        <span class="value">{{ formatCoins(handoff.provider_sent_coins) }} / {{ formatCoins(handoff.provider_received_coins) }}</span>
      </div>
      @if (handoff.free_slots_before_submit !== undefined) {
      <div class="detail">
        <span class="label">Free Slots Before Submit</span>
        <span class="value">{{ handoff.free_slots_before_submit }}</span>
      </div>
      }
      @if (handoff.provider_status) {
      <div class="detail">
        <span class="label">Provider Status</span>
        <span class="value">{{ handoff.provider_status }}</span>
      </div>
      }
      @if (handoff.provider_account_state) {
      <div class="detail">
        <span class="label">Provider Account</span>
        <span class="value">{{ handoff.provider_account_state }}</span>
      </div>
      }
      @if (handoff.provider_economy_state) {
      <div class="detail">
        <span class="label">Provider Economy</span>
        <span class="value">{{ handoff.provider_economy_state }}</span>
      </div>
      }
      <div class="detail">
        <span class="label">Created</span>
        <span class="value">{{ formatDate(handoff.created_at) }}</span>
      </div>
      <div class="detail">
        <span class="label">Updated</span>
        <span class="value">{{ formatDate(handoff.updated_at) }}</span>
      </div>
      @if (handoff.finished_at) {
      <div class="detail">
        <span class="label">Finished</span>
        <span class="value">{{ formatDate(handoff.finished_at) }}</span>
      </div>
      }
    </div>

    @if (handoff.status === 'NeedsReview') {
    <div class="action-section">
      <button mat-stroked-button color="primary" [disabled]="actionLoading" (click)="retry()">
        @if (actionLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
        @else { <mat-icon>replay</mat-icon> }
        Retry
      </button>
    </div>
    }

    @if (handoff.status === 'NeedsCredentials') {
    <div class="action-section creds-form">
      <h4>Correct Credentials</h4>
      <mat-form-field appearance="outline">
        <mat-label>New Password</mat-label>
        <input matInput type="password" [(ngModel)]="credsPassword" placeholder="Leave blank to keep unchanged" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Backup Codes</mat-label>
        <input matInput [(ngModel)]="credsBackupCodes" (paste)="onBackupCodesPaste($event)" placeholder="code1, code2, ... (1-6, comma-separated)" />
      </mat-form-field>
      <button mat-flat-button color="primary" [disabled]="actionLoading" (click)="submitCredentials()">
        @if (actionLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
        Update Credentials
      </button>
    </div>
    }

    @if (actionError) {
    <div class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ actionError }}</span>
    </div>
    }

    <div class="screenshots-section">
      <div class="screenshot-controls">
        <button mat-stroked-button [disabled]="startScreenshotLoading" (click)="loadScreenshot('start')">
          @if (startScreenshotLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
          @else { <mat-icon>photo_camera</mat-icon> }
          Start Screenshot
        </button>
        <button mat-stroked-button [disabled]="endScreenshotLoading" (click)="loadScreenshot('end')">
          @if (endScreenshotLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
          @else { <mat-icon>photo_camera</mat-icon> }
          End Screenshot
        </button>
      </div>
      @if (startScreenshotError) {
      <p class="screenshot-error">{{ startScreenshotError }}</p>
      }
      @if (endScreenshotError) {
      <p class="screenshot-error">{{ endScreenshotError }}</p>
      }
      <div class="screenshots">
        @if (startScreenshotUrl) {
        <div class="screenshot-item">
          <p class="screenshot-label">Start</p>
          <img [src]="startScreenshotUrl" alt="Start screenshot" class="screenshot-img" />
        </div>
        }
        @if (endScreenshotUrl) {
        <div class="screenshot-item">
          <p class="screenshot-label">End</p>
          <img [src]="endScreenshotUrl" alt="End screenshot" class="screenshot-img" />
        </div>
        }
      </div>
    </div>

    @if (!isTerminal) {
    <p class="polling-hint">
      <mat-icon>autorenew</mat-icon>
      Auto-updating every 5 seconds
    </p>
    }
  </div>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="close()">Close</button>
</mat-dialog-actions>
